import { common } from '@mui/material/colors';
import { PaletteColor, alpha, createTheme } from '@mui/material/styles';

import {
  BRONZE,
  GOLD,
  GREEN,
  GREY,
  INTRINSIC_GREY,
  LIGHT_BLUE,
  MECHA_METAL,
  ORANGE,
  PLATINUM,
  RED,
  TEAL,
} from './colours';
import { DARK_THEME_COLOURS, DARK_THEME_GREY, createDarkModeComponentOverrides } from './darkTheme';

interface PaletteColorWithGradient extends PaletteColor {
  gradient: string;
}

declare module '@mui/material/styles' {
  interface Palette {
    neutral: PaletteColor;
    tertiary: PaletteColor;
    luxPlus: PaletteColor;
    loyaltyTiers: {
      bronze: PaletteColorWithGradient;
      silver: PaletteColorWithGradient;
      gold: PaletteColorWithGradient;
      platinum: PaletteColorWithGradient;
      infinite: PaletteColorWithGradient;
    };
  }

  interface PaletteOptions {
    neutral: PaletteColor;
    tertiary: PaletteColor;
    luxPlus: PaletteColor;
    loyaltyTiers: {
      bronze: PaletteColorWithGradient;
      silver: PaletteColorWithGradient;
      gold: PaletteColorWithGradient;
      platinum: PaletteColorWithGradient;
      infinite: PaletteColorWithGradient;
    };
  }
}
declare module '@mui/material/AppBar' {
  interface AppBarPropsColorOverrides {
    neutral: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    neutral: true;
  }
}

export const lightTheme = createTheme({
  typography: {
    fontFamily: '"Suisse Intl", Arial, sans-serif',
    allVariants: {
      fontFamily: '"Suisse Intl", Arial, sans-serif',
    },
    h6: {
      fontSize: '1rem', // Set to match the figma file
    },
    body2: {
      fontSize: '0.875rem', // Set to match figma file = 14px
    },
  },
  palette: {
    mode: 'light',
    text: {
      primary: GREY[700],
      secondary: GREY[600],
      disabled: GREY[500],
    },
    primary: {
      main: TEAL[500],
      dark: TEAL[600],
      light: TEAL[200],
      contrastText: common.white,
    },
    secondary: {
      main: GREY[700],
      dark: GREY[800],
      light: GREY[300],
      contrastText: common.white,
    },
    tertiary: {
      main: GREY[500],
      dark: GREY[600],
      light: GREY[200],
      contrastText: common.white,
    },
    error: {
      main: RED[500],
      dark: RED[900],
      light: RED[50],
      contrastText: common.white,
    },
    warning: {
      main: ORANGE[500],
      dark: ORANGE[900],
      light: ORANGE[50],
      contrastText: common.white,
    },
    info: {
      main: LIGHT_BLUE[500],
      dark: LIGHT_BLUE[900],
      light: LIGHT_BLUE[50],
      contrastText: common.white,
    },
    success: {
      main: GREEN[500],
      dark: GREEN[900],
      light: GREEN[50],
      contrastText: common.white,
    },
    neutral: {
      main: common.white,
      light: common.white,
      dark: INTRINSIC_GREY[100],
      contrastText: common.black,
    },
    luxPlus: {
      main: '#304DAB',
      contrastText: common.white,
      light: '#5DA4FA',
      dark: '#0F1C47',
    },
    loyaltyTiers: {
      bronze: {
        main: BRONZE[300],
        contrastText: common.black,
        light: BRONZE[100],
        dark: BRONZE[500],
        gradient: `linear-gradient(45deg, ${BRONZE[300]} 0.73%, ${BRONZE[100]} 45.77%, ${BRONZE[300]} 74.49%)`,
      },
      silver: {
        main: MECHA_METAL[300],
        contrastText: common.black,
        light: MECHA_METAL[100],
        dark: MECHA_METAL[500],
        gradient: `linear-gradient(45deg, ${MECHA_METAL[300]} 0.73%, ${MECHA_METAL[100]} 45.77%, ${MECHA_METAL[300]} 74.49%)`,
      },
      gold: {
        main: GOLD[300],
        contrastText: common.black,
        light: GOLD[100],
        dark: GOLD[500],
        gradient: `linear-gradient(45deg, ${GOLD[300]} 0.73%, ${GOLD[100]} 45.77%, ${GOLD[300]} 74.49%)`,
      },
      platinum: {
        main: PLATINUM[600],
        contrastText: common.white,
        light: PLATINUM[400],
        dark: PLATINUM[700],
        gradient: `linear-gradient(45deg, ${PLATINUM[900]} 0.73%, ${PLATINUM[600]} 45.77%, ${PLATINUM[900]} 74.49%)`,
      },
      infinite: {
        main: '#4A443F',
        contrastText: common.white,
        light: '#4A443F',
        dark: '#030100',
        gradient: 'linear-gradient(45deg, #030100 0.73%, #4A443F 45.77%, #030100 74.49%)',
      },
    },
  },
  components: {
    MuiContainer: {
      defaultProps: {
        maxWidth: false,
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderRadius: 0,
          border: '1px solid',
          borderColor: alpha(GREY[700], 0.08),
          backgroundColor: alpha(GREY[700], 0.04),
          '&.Mui-expanded': {
            backgroundColor: alpha(TEAL[500], 0.08),
          },
          '&:hover': {
            backgroundColor: alpha(TEAL[500], 0.04),
          },
          '&.Mui-expanded, &:hover': {
            borderColor: alpha(TEAL[500], 0.08),
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          paddingTop: '1.5rem',
          backgroundColor: common.white,
          overflow: 'auto',
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        expandIconWrapper: {
          color: TEAL[500],
        },
        root: {
          minHeight: '2.25rem !important',
        },
        content: {
          marginTop: 0,
          marginBottom: 0,
          '&.Mui-expanded': {
            marginTop: 0,
            marginBottom: 0,
          },
        },
      },
    },
    MuiAppBar: {
      variants: [{ props: { color: 'neutral' }, style: {} }],
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: GREY[700],
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
      variants: [
        {
          props: { variant: 'contained', color: 'neutral' },
          style: {},
        },
      ],
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: common.white,
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: '8px 16px 16px',
        },
      },
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: { variant: 'h6' },
      },
      styleOverrides: {
        root: {
          '& + .MuiCardContent-root': {
            paddingTop: 0,
          },
        },
        action: {
          alignSelf: 'unset',
          margin: 0,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          '&:last-child': {
            paddingBottom: '16px',
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          backgroundColor: common.white,
          borderColor: 'transparent',
        },
        columnHeader: (params) => ({
          backgroundColor: 'transparent',
          color: params.theme.palette.text.primary,
          typography: {
            fontWeight: 400,
          },
        }),
        cell: {
          display: 'flex',
          alignItems: 'center',
        },
        menuIconButton: (params) => ({
          color: params.theme.palette.primary.main,
        }),
        sortIcon: (params) => ({
          color: params.theme.palette.primary.main,
        }),
        menuIcon: (params) => ({
          color: params.theme.palette.primary.main,
        }),
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '8px 24px 24px',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          '.MuiDialogTitle-root + &': {
            paddingTop: '8px',
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: common.white,
          border: 'none',
          boxSizing: 'border-box',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          '&:hover': {
            backgroundColor: alpha(TEAL[500], 0.04),
          },
          '&.Mui-selected': {
            backgroundColor: alpha(TEAL[500], 0.08),
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: '14px',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: GREY[700],
          color: common.white,
          padding: '8px 16px',
        },
      },
    },
    MuiTypography: {
      variants: [
        {
          props: { variant: 'overline' },
          style: {
            lineHeight: '11px',
            fontWeight: 500,
            fontSize: '11px',
            letterSpacing: '0.7px',
            color: GREY[500],
          },
        },
      ],
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '2px',
        },
        standardSuccess: {
          backgroundColor: GREEN[50],
          color: GREEN[900],
        },
        standardWarning: {
          backgroundColor: 'transparent',
          color: ORANGE[900],
          border: `1px solid ${ORANGE[500]}`,
        },
        standardError: {
          backgroundColor: RED[50],
          color: RED[900],
        },
        standardInfo: {
          backgroundColor: LIGHT_BLUE[50],
          color: LIGHT_BLUE[900],
        },
      },
    },
    MuiStack: {
      styleOverrides: {
        root: {
          maxWidth: '100%',
          overflow: 'auto',
          // Stacks clip overflow by default in this theme, but MUI outlined labels float above their form controls.
          // Allow overflow only for stacks containing labelled controls so labels are not cut off.
          '&:has(.MuiFormControl-root > .MuiInputLabel-root)': {
            overflow: 'visible',
          },
          '& > *': {
            minWidth: 0,
          },
        },
      },
    },
  },
});

export const darkTheme = createTheme(lightTheme, {
  palette: {
    mode: 'dark',
    background: {
      default: DARK_THEME_COLOURS.backgroundDefault,
      paper: DARK_THEME_COLOURS.backgroundPaper,
    },
    text: {
      primary: DARK_THEME_COLOURS.textPrimary,
      secondary: DARK_THEME_COLOURS.textSecondary,
      disabled: DARK_THEME_COLOURS.textDisabled,
    },
    grey: DARK_THEME_GREY,
    primary: {
      main: TEAL[200],
      dark: TEAL[400],
      light: TEAL[100],
      contrastText: GREY[900],
    },
    secondary: {
      main: GREY[100],
      dark: GREY[300],
      light: GREY[500],
      contrastText: GREY[900],
    },
    neutral: {
      main: DARK_THEME_COLOURS.backgroundPaper,
      light: DARK_THEME_COLOURS.backgroundElevated,
      dark: DARK_THEME_COLOURS.backgroundDefault,
      contrastText: common.white,
    },
    divider: alpha(common.white, 0.16),
    action: {
      active: '#d7dde5',
      hover: alpha(common.white, 0.08),
      selected: alpha(TEAL[200], 0.16),
      disabled: alpha(common.white, 0.32),
      disabledBackground: alpha(common.white, 0.12),
    },
  },
  components: createDarkModeComponentOverrides(TEAL[100]),
});
